/**
 * HealthBar component - displays character health as a visual bar.
 * Color-coded: green (>70%), yellow (30-70%), red (<30%), gray (incapacitated).
 */

import type { CharacterHealthInfo } from '@/types/characters';

interface HealthBarProps {
    /** Character health information */
    health: CharacterHealthInfo;
    /** Show numeric values (default: true) */
    showValues?: boolean;
    /** Size variant: 'small' for compact display, 'large' for detailed display */
    size?: 'small' | 'large';
}

/**
 * Get the color classes based on health percentage.
 */
function getHealthColors(percentage: number, isIncapacitated: boolean): {
    bar: string;
    text: string;
    glow: string;
} {
    if (isIncapacitated) {
        return {
            bar: 'bg-gray-500',
            text: 'text-gray-600',
            glow: 'shadow-gray-500/30',
        };
    }
    if (percentage >= 70) {
        return {
            bar: 'bg-emerald-500',
            text: 'text-emerald-700',
            glow: 'shadow-emerald-500/30',
        };
    }
    if (percentage >= 30) {
        return {
            bar: 'bg-amber-500',
            text: 'text-amber-700',
            glow: 'shadow-amber-500/30',
        };
    }
    return {
        bar: 'bg-red-500',
        text: 'text-red-700',
        glow: 'shadow-red-500/30',
    };
}

/**
 * Displays character health as a styled bar with optional numeric values.
 */
export function HealthBar({ health, showValues = true, size = 'small' }: HealthBarProps) {
    const { current, max, percentage, is_incapacitated } = health;
    const colors = getHealthColors(percentage, is_incapacitated);

    const barHeight = size === 'large' ? 'h-4' : 'h-2.5';
    const fontSize = size === 'large' ? 'text-base' : 'text-sm';

    return (
        <div className="flex items-center gap-2">
            {/* Health bar */}
            <div className="flex-1 relative">
                {/* Bar background */}
                <div
                    className={`${barHeight} rounded-full overflow-hidden`}
                    style={{
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 100%)',
                        border: '1px solid rgba(139,115,85,0.3)',
                    }}
                >
                    {/* Health fill */}
                    <div
                        className={`h-full ${colors.bar} transition-all duration-300 ${colors.glow}`}
                        style={{
                            width: `${Math.max(0, Math.min(100, percentage))}%`,
                            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.3), 0 0 4px ${colors.glow}`,
                        }}
                    />
                </div>

                {/* Incapacitated overlay */}
                {is_incapacitated && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span
                            className="text-[10px] font-serif font-bold uppercase tracking-wider text-gray-700"
                            style={{ textShadow: '0 1px 0 rgba(255,255,255,0.5)' }}
                        >
                            Incapacitated
                        </span>
                    </div>
                )}
            </div>

            {/* Numeric values */}
            {showValues && (
                <div className={`font-serif font-semibold ${fontSize} ${colors.text} whitespace-nowrap`}>
                    {current}/{max}
                </div>
            )}
        </div>
    );
}

/**
 * Compact health indicator for use in lists or tight spaces.
 * Shows a small heart icon with color-coded fill.
 */
export function HealthIndicator({ health }: { health: CharacterHealthInfo }) {
    const { percentage, is_incapacitated } = health;
    const colors = getHealthColors(percentage, is_incapacitated);

    return (
        <div className="relative inline-flex items-center gap-1" title={`${health.current}/${health.max} HP`}>
            {/* Heart icon */}
            <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="currentColor"
            >
                {/* Background heart (empty) */}
                <path
                    className="text-gray-300"
                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                />
                {/* Filled heart (clipped based on health) */}
                <defs>
                    <clipPath id={`health-clip-${percentage}`}>
                        <rect x="0" y={24 - (24 * percentage / 100)} width="24" height={24 * percentage / 100} />
                    </clipPath>
                </defs>
                <path
                    className={colors.text}
                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                    clipPath={`url(#health-clip-${percentage})`}
                />
            </svg>
            {is_incapacitated && (
                <span className="text-[9px] font-bold text-gray-500 uppercase">KO</span>
            )}
        </div>
    );
}

export default HealthBar;
